import { Box, Button, Typography } from "@mui/material";
import { useEffect } from "react";

import SimpleModal from "@/components/SimpleModal.tsx";
import { config } from "@/config";
import { useGlobalFlags } from "@/hooks/useGlobalFlags";
import { useSafeLocalStorage } from "@/hooks/useSafeLocalStorage";

const MAX_RELOADS = 5;
const RELOAD_DELAY_MS = 60_000; // 1 minute

/**
 * Automatically reloads the page when the LaunchDarkly `minimumReleaseTag` flag
 * exceeds the numeric release tag baked into the current build (`config.releaseTag`).
 *
 * Waits RELOAD_DELAY_MS between each attempt and auto-reloads up to MAX_RELOADS times
 * (tracked in localStorage) to avoid infinite loops if the new version can't be fetched.
 * After exhausting attempts, falls back to a dialog prompting the user to reload manually.
 */
export default function VersionCheckDialog() {
  const { minimumReleaseTag } = useGlobalFlags();
  const minimumVersion = minimumReleaseTag ? Number(minimumReleaseTag) : 0;
  const [reloadCount, setReloadCount] = useSafeLocalStorage("versionReloadCount", 0);
  const [reloadTarget, setReloadTarget] = useSafeLocalStorage("versionReloadTarget", 0);

  const flagsLoaded = minimumVersion > 0;
  const isStale = Boolean(flagsLoaded && config.releaseTag && minimumVersion > config.releaseTag);

  const isNewTarget = minimumVersion !== reloadTarget;
  const exhaustedReloads = !isNewTarget && reloadCount >= MAX_RELOADS;

  useEffect(() => {
    if (!flagsLoaded) return;

    if (!isStale) {
      if (reloadCount > 0 || reloadTarget !== 0) {
        setReloadCount(0);
        setReloadTarget(0);
      }
      return;
    }

    if (exhaustedReloads) return;

    const reload = () => {
      if (isNewTarget) {
        setReloadTarget(minimumVersion);
        setReloadCount(1);
      } else {
        setReloadCount((prev) => prev + 1);
      }
      window.location.reload();
    };

    const timeout = setTimeout(reload, RELOAD_DELAY_MS);
    return () => clearTimeout(timeout);
  }, [flagsLoaded, isStale, minimumVersion]);

  const handleRetry = () => {
    setReloadCount(0);
    window.location.reload();
  };

  return (
    <SimpleModal
      open={isStale && exhaustedReloads}
      closeModal={() => {}}
      title="Update Required"
      sx={{ maxWidth: "420px" }}
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2, py: 1 }}>
        <Typography variant="body1">
          A new version of the application is available but could not be loaded automatically. Please try reloading
          manually.
        </Typography>
        <Typography sx={{ fontSize: "14px", color: "text.secondary" }}>
          If you still see this message try clearing your browser cache and reloading.
        </Typography>
        <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
          <Button variant="contained" color="primary" onClick={handleRetry}>
            Reload Now
          </Button>
        </Box>
      </Box>
    </SimpleModal>
  );
}
